import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { bool, node } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  NamedLink,
  FieldSelect,
  IconEdit,
  IconInquiry,
  BirthdayInput,
  FieldRadioButton,
  IconSpinner,
  FieldCountrySelect,
} from '../../../components';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { pathByRouteName } from '../../../util/routes';
import css from './SignupForm.module.css';
import IconEyeOpen from '../../../components/IconEyeOpen/IconEyeOpen';
import IconEyeClose from '../../../components/IconEyeClose/IconEyeClose';
import IconInfo from '../../../components/IconInfo/IconInfo';
import { useConfiguration } from '../../../context/configurationContext';
import { USER_TYPE } from '../../../helpers/enums';
import moment from 'moment';
import {
  DEFAULT_LANG_OPTIONS,
  getPatientOptionsByLang,
  userTypeOptions,
} from '../../../config/customConfig';
import {
  isLocaleGreek,
  sortArrayAlphabetically,
  getDefaultLocale,
  isBioiatriki,
  switchAppLanguage,
} from '../../../util/genericHelpers';
import SidebarProvider from '../SidebarProvider';
import SidebarUser from '../SidebarUser';
import { useHistory, useParams, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import RenderVoucherOrId from './RenderVoucher';
import { useDispatch, useSelector } from 'react-redux';
import { verifyCodeSelector } from '../../../ducks/global.duck';
import { DOCTOR_PROVIDER_ROUTE, MIXPANEL_EVENTS } from '../../../util/enums';
import { getUrlForLocale } from '../../../helpers/convert';
import Icons from '../../../components/Icons/Icons';
import { checkIfEmailExists } from '../../../ducks/global.duck';
import { trackRegisterEvent } from '../../../util/mixpanelHelpers';
import { getDefaultWebSubscriptionPlan } from '../../../util/webSubscriptionVisibility';

const useQueryParams = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        userType,
        viewport,
        form,
        values,
        hideUserType,
        hideCouponCode = false,
        isProgram = false,
        handleCancel = () => {},
        isSubscribeDisabled = false,
        paymentInProgress = false,
        subscriptionUserType = 'regular',
        onSubscriptionTypeChange = () => {},
        productPrices = [],
        plan = null,
        setPlan = () => {},
        couponInputId, // <-- injected from ProgramSection
        couponPlanError = null, // <-- NEW: red mismatch message from ProgramSection
        isCustomLandingPage,
        forceCorporateCoupon = false,
      } = fieldRenderProps;

      const history = useHistory();
      const params = useParams();
      const isGreek = isLocaleGreek();
      const routeConfiguration = useRouteConfiguration();
      const config = useConfiguration();
      const queryParams = useQueryParams();
      const location = useLocation();
      const { state } = location;
      const dispatch = useDispatch();

      const { initialVoucherSource, autoVerifyVoucher } = props;

      const safeChange = useCallback(
        (field, value) => {
          if (!form || typeof form.change !== 'function') return;
          if (typeof form.getState === 'function' && !form.getState()) return;
          form.change(field, value);
        },
        [form]
      );

      const isFromDoctors = state?.from === DOCTOR_PROVIDER_ROUTE;
      const locale = config.localization.locale;
      const isEnglish = locale === 'en';
      const isDoctorOnlySignup =
        userType === USER_TYPE.DOCTOR || params?.type === USER_TYPE.DOCTOR;
      const shouldHideUserTypeOptions =
        hideUserType || isFromDoctors || isDoctorOnlySignup || isProgram;
      const isCorporateSignup = forceCorporateCoupon || subscriptionUserType === 'corporate';

      const [showPassword, setShowPassword] = useState(false);
      const [showMoreContent, setShowMoreContent] = useState(false);
      const [invalidDate, setInvalidDate] = useState(false);
      const [restoredDateOfBirth, setRestoredDateOfBirth] = useState(null);
      const [hasRestoredData, setHasRestoredData] = useState(false);
      const [restoredLanguage, setRestoredLanguage] = useState(null);

      // --- Email availability state (debounced) ---
      const [emailCheckLoading, setEmailCheckLoading] = useState(false);
      const [emailTaken, setEmailTaken] = useState(false);
      const [lastCheckedEmail, setLastCheckedEmail] = useState('');

      const {
        inProgress: voucherVerificationInProgress = null,
        success: voucherSuccess = false,
        isCodeValid = false,
      } = useSelector(verifyCodeSelector) || {};

      // Ensure coupon input has id and 100% width even if RenderVoucherOrId isn't updated yet
      useEffect(() => {
        const el =
          (couponInputId && document.getElementById(couponInputId)) ||
          document.querySelector('input[name="voucher"]');
        if (el) {
          if (couponInputId && !el.id) el.id = couponInputId;
          // make 100% width as a safe fallback
          el.style.width = '100%';
          el.style.boxSizing = 'border-box';
        }
      }, [couponInputId]);

      // Handle subscription type change
      const handleSubscriptionTypeChange = type => {
        const nextType = forceCorporateCoupon ? 'corporate' : type;
        if (nextType === 'corporate') {
          trackRegisterEvent(MIXPANEL_EVENTS.REGISTER_WITH_COUPON);
        }
        onSubscriptionTypeChange(nextType);
        safeChange('subscriptionType', nextType);
        if (productPrices?.length > 0) {
          const nextPlan = getDefaultWebSubscriptionPlan({
            productPrices,
            preferredInterval: nextType === 'corporate' ? 'year' : 'month',
            isCorporateUser: nextType === 'corporate',
          });
          if (nextPlan) {
            setPlan(nextPlan);
          }
        }
      };

      useEffect(() => {
        safeChange('subscriptionType', forceCorporateCoupon ? 'corporate' : subscriptionUserType);
      }, [forceCorporateCoupon, safeChange, subscriptionUserType]);

      const handleLanguageChange = selectedLanguage => {
        if (selectedLanguage && selectedLanguage !== '') {
          if (hasRestoredData && restoredLanguage === selectedLanguage) return;

          const languageToLocaleMap = { gr: 'el', en: 'en', de: 'de' };
          const newLocale = languageToLocaleMap[selectedLanguage];

          if (newLocale && newLocale !== getDefaultLocale()) {
            if (typeof window !== 'undefined') {
              const formDataToSave = {
                fname: values?.fname || '',
                lname: values?.lname || '',
                email: values?.email || '',
                password: values?.password || '',
                dateOfBirth: values?.dateOfBirth || '',
                city: values?.city || '',
                country: values?.country || '',
                speciality: values?.speciality || '',
                preferredLang: selectedLanguage,
                type: values?.type || '',
                voucher: values?.voucher || '',
                voucherId: values?.voucherId || '',
                subscriptionType: forceCorporateCoupon
                  ? 'corporate'
                  : values?.subscriptionType || 'regular',
                timestamp: Date.now(),
              };

              window.localStorage.setItem('signupFormData', JSON.stringify(formDataToSave));
              const pathname = window.location.pathname;
              switchAppLanguage({ locale: newLocale, pathname });
            }
          }
        }
      };

      useEffect(() => {
        const typeParam = queryParams.get('userType');
        const desiredType = typeParam === 'user' ? USER_TYPE.INDIVIDUAL : params?.type;
        if (desiredType && values?.type !== desiredType) {
          safeChange('type', desiredType);
        }
      }, [queryParams, safeChange, params?.type, values?.type]);

      // If this is a provider-only signup entry point, force the user type and hide the tabs.
      useEffect(() => {
        if (isDoctorOnlySignup && values?.type !== USER_TYPE.DOCTOR) {
          safeChange('type', USER_TYPE.DOCTOR);
        }
      }, [isDoctorOnlySignup, values?.type, safeChange]);

      useEffect(() => {
        if (typeof window !== 'undefined' && !hasRestoredData) {
          const savedFormData = window.localStorage.getItem('signupFormData');
          if (savedFormData) {
            try {
              const parsedData = JSON.parse(savedFormData);
              const isDataFresh =
                parsedData.timestamp && Date.now() - parsedData.timestamp < 30 * 60 * 1000;

              if (isDataFresh) {
                const languageToLocaleMap = { gr: 'el', en: 'en', de: 'de' };
                const savedLocale = languageToLocaleMap[parsedData.preferredLang];
                const currentLocale = getDefaultLocale();

                if (savedLocale !== currentLocale) {
                  window.localStorage.removeItem('signupFormData');
                  setHasRestoredData(true);
                  return;
                }

                setTimeout(() => {
                  Object.keys(parsedData).forEach(key => {
                    if (
                      parsedData[key] &&
                      key !== 'preferredLang' &&
                      key !== 'timestamp' &&
                      key !== 'salutation' &&
                      key !== 'title' &&
                      !(forceCorporateCoupon && key === 'subscriptionType')
                    ) {
                      safeChange(key, parsedData[key]);
                    }
                  });

                  if (parsedData.dateOfBirth) {
                    const restoredDate = moment(parsedData.dateOfBirth).toDate();
                    setRestoredDateOfBirth(restoredDate);
                    handleDateOfBirthChange(restoredDate);
                  }

                  if (parsedData.preferredLang) {
                    setRestoredLanguage(parsedData.preferredLang);
                    safeChange('preferredLang', parsedData.preferredLang);
                  }

                  if (parsedData.subscriptionType || forceCorporateCoupon) {
                    handleSubscriptionTypeChange(
                      forceCorporateCoupon ? 'corporate' : parsedData.subscriptionType
                    );
                  }

                  window.localStorage.removeItem('signupFormData');
                  setHasRestoredData(true);
                }, 100);
              } else {
                window.localStorage.removeItem('signupFormData');
                setHasRestoredData(true);
              }
            } catch (error) {
              setHasRestoredData(true);
            }
          } else {
            setHasRestoredData(true);
          }
        }
      }, [form, hasRestoredData]);

      useEffect(() => {
        if (restoredLanguage && !hasRestoredData) {
          safeChange('preferredLang', restoredLanguage);
        }
      }, [restoredLanguage, safeChange, hasRestoredData]);

      const fromParam = queryParams.get('from');
      const isFromBooking = fromParam === 'booking';

      // email
      const emailRequired = validators.required(
        intl.formatMessage({ id: 'SignupForm.emailRequired' })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({ id: 'SignupForm.emailInvalid' })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({ id: 'SignupForm.passwordRequired' });
      const passwordMinLengthMessage = intl.formatMessage(
        { id: 'SignupForm.passwordTooShort' },
        { minLength: validators.PASSWORD_MIN_LENGTH }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        { id: 'SignupForm.passwordTooLong' },
        { maxLength: validators.PASSWORD_MAX_LENGTH }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      const handleDateOfBirthChange = newDate => {
        const dob = moment(newDate).format('YYYY-MM-DD');
        safeChange('dateOfBirth', dob);
        const age = moment().diff(moment(newDate), 'years');
        setInvalidDate(age < 18);
      };

      // ---------- Debounced email availability check ----------
      useEffect(() => {
        const rawEmail = (values?.email || '').trim();
        // if empty or clearly invalid, reset state & skip
        if (!rawEmail || emailValid(rawEmail)) {
          setEmailTaken(false);
          setLastCheckedEmail('');
          setEmailCheckLoading(false);
          return;
        }

        setEmailCheckLoading(true);
        const t = setTimeout(async () => {
          try {
            const res = await dispatch(checkIfEmailExists(rawEmail));
            setLastCheckedEmail(rawEmail);
            setEmailTaken(!!res?.exists);
          } finally {
            setEmailCheckLoading(false);
          }
        }, 600);

        return () => clearTimeout(t);
      }, [values?.email, dispatch]);

      // validator that surfaces "email already registered"
      const emailNotTakenValidator = useCallback(
        value => {
          if (!value) return undefined;
          if (value === lastCheckedEmail && emailTaken) {
            return intl.formatMessage({
              id: 'SignupForm.emailAlreadyRegistered',
              defaultMessage: 'This email is already registered.',
            });
          }
          return undefined;
        },
        [lastCheckedEmail, emailTaken, intl]
      );

      // Compose once, but recompute when deps change so Field revalidates
      const emailValidatorsCombined = useMemo(
        () => validators.composeValidators(emailRequired, emailValid, emailNotTakenValidator),
        [emailRequired, emailValid, emailNotTakenValidator]
      );

      const classes = classNames(
        rootClassName || css.root,
        className,
        isCustomLandingPage ? css.customLandingPage : null
      );
      const submitInProgress = inProgress;
      const submitDisabled =
        invalid ||
        submitInProgress ||
        emailCheckLoading || // also disable while checking to avoid race
        (emailTaken && values?.email === lastCheckedEmail);

      const renderDoctorFields = (
        <>
          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <FieldSelect
                id={formId ? `${formId}.speciality` : 'speciality'}
                name="speciality"
                onChange={speciality => safeChange('speciality', speciality)}
              >
                <option value="" disabled>
                  {intl.formatMessage({ id: 'AuthenticationPage.chooseSpeciality' })}
                </option>
                {getPatientOptionsByLang().map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </FieldSelect>
            </div>
          </div>
        </>
      );

      const renderIndividualsFields = (
        <>
          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <BirthdayInput
                intl={intl}
                onDateChange={handleDateOfBirthChange}
                initialValue={restoredDateOfBirth}
              />
            </div>
            {invalidDate ? (
              <span className={css.error}>
                {intl.formatMessage({ id: 'SignupForm.yearValidation' })}
              </span>
            ) : null}
          </div>
          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <FieldTextInput
                className={css.firstNameRoot}
                type="text"
                id={formId ? `${formId}.city` : 'city'}
                name="city"
                placeholder={intl.formatMessage({ id: 'SignupForm.city' })}
                validate={validators.required(
                  intl.formatMessage({ id: 'SignupForm.cityRequired' })
                )}
              />
            </div>
          </div>
          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <FieldCountrySelect
                id={formId ? `${formId}.country` : 'country'}
                name="country"
                locale={locale}
                placeholder={intl.formatMessage({
                  id: 'StripeConnectAccountForm.countryPlaceholder',
                })}
                intl={intl}
                validate={validators.required(
                  intl.formatMessage({ id: 'SignupForm.countryRequired' })
                )}
              />
            </div>
          </div>
        </>
      );

      return (
        <>
          {isCustomLandingPage ? (
            <>
              <div className={css.contactHeading}>
                <h1>
                  {isFromBooking ? (
                    <FormattedMessage id="AuthenticationPage.signupToBook" />
                  ) : (
                    <FormattedMessage id="AuthenticationPage.signupForm" />
                  )}
                </h1>
              </div>
              <div className={css.contactFormSec}>
                <div className={css.contactForm}>
                  <Form className={classes} onSubmit={handleSubmit} data-testid="signup-form">
                    <div className={css.signupForm}>
                      {isProgram && !forceCorporateCoupon && (
                        <div className={css.subscriptionToggle}>
                          <button
                            type="button"
                            className={classNames(css.toggleOption, {
                              [css.active]: subscriptionUserType === 'regular',
                            })}
                            onClick={() => handleSubscriptionTypeChange('regular')}
                          >
                            {intl.formatMessage({ id: 'SignupForm.regularUser' })}
                          </button>
                          <button
                            type="button"
                            className={classNames(css.toggleOption, {
                              [css.active]: subscriptionUserType === 'corporate',
                            })}
                            onClick={() => handleSubscriptionTypeChange('corporate')}
                          >
                            {intl.formatMessage({ id: 'SignupForm.corporateUser' })}
                          </button>
                        </div>
                      )}

                      {!shouldHideUserTypeOptions && (
                        <div className={css.typeOptions}>
                          {userTypeOptions?.map((u, i) => (
                            <FieldRadioButton
                              key={i}
                              id={u.key}
                              name="type"
                              label={isGreek ? u.greekLabel : isEnglish ? u.label : u.germanLabel}
                              value={u.key}
                              validate={validators.required(
                                intl.formatMessage({ id: 'SignupForm.userTypeOptions' })
                              )}
                            />
                          ))}
                        </div>
                      )}

                      <div className={classNames(css.formRow, css.nameRow)}>
                        <div className={css.formFld}>
                          <FieldTextInput
                            type="text"
                            id={formId ? `${formId}.fname` : 'fname'}
                            name="fname"
                            autoComplete="given-name"
                            placeholder={intl.formatMessage({
                              id: 'SignupForm.firstNamePlaceholder',
                            })}
                            validate={validators.required(
                              intl.formatMessage({ id: 'SignupForm.firstNameRequired' })
                            )}
                          />
                        </div>
                        <div className={css.formFld}>
                          <FieldTextInput
                            type="text"
                            id={formId ? `${formId}.lname` : 'lname'}
                            name="lname"
                            autoComplete="family-name"
                            placeholder={intl.formatMessage({
                              id: 'SignupForm.lastNamePlaceholder',
                            })}
                            validate={validators.required(
                              intl.formatMessage({ id: 'SignupForm.lastNameRequired' })
                            )}
                          />
                        </div>
                      </div>

                      <div
                        className={classNames(css.formRow, css.fullWidth)}
                        style={{ position: 'relative' }}
                      >
                        <div className={css.formFld}>
                          <FieldTextInput
                            type="email"
                            name="email"
                            id="email-signup"
                            data-testid="signup-email"
                            autoComplete="email"
                            placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder' })}
                            validate={emailValidatorsCombined}
                          />

                          {emailCheckLoading && (
                            <span
                              style={{
                                position: 'absolute',
                                right: 10,
                                top: '50%',
                                transform: 'translateY(-50%)',
                              }}
                            >
                              <IconSpinner />
                            </span>
                          )}
                        </div>
                      </div>

                      <div className={classNames(css.formRow, css.fullWidth)}>
                        <div className={css.formFld}>
                          <FieldTextInput
                            type={showPassword ? 'text' : 'password'}
                            id={formId ? `${formId}.password` : 'password'}
                            name="password"
                            data-testid="signup-password"
                            autoComplete="new-password"
                            placeholder={intl.formatMessage({
                              id: 'SignupForm.passwordPlaceholder',
                            })}
                            validate={passwordValidators}
                          />
                          <span
                            className={css.showHidePass}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <IconEyeClose /> : <IconEyeOpen />}
                          </span>
                        </div>
                      </div>

                      {userType === USER_TYPE.INDIVIDUAL ? renderIndividualsFields : null}
                      {userType === USER_TYPE.DOCTOR ? renderDoctorFields : null}

                      {!isBioiatriki() && (
                        <div className={classNames(css.formRow, css.fullWidth)}>
                          <div className={css.formFld}>
                            <FieldSelect
                              id={formId ? `${formId}.preferredLang` : 'preferredLang'}
                              name="preferredLang"
                              validate={validators.required(
                                intl.formatMessage({ id: 'SignupForm.preferredLangRequired' })
                              )}
                              onChange={handleLanguageChange}
                            >
                              {DEFAULT_LANG_OPTIONS.map(s => (
                                <option key={s.key} value={s.key} disabled={s?.disabled}>
                                  {isGreek ? s.greekLabel : isEnglish ? s.label : s.germanLabel}
                                </option>
                              ))}
                            </FieldSelect>
                            <p className={css.languageInfo}>
                              {values?.preferredLang === 'gr' ? (
                                intl.formatMessage({ id: 'SignupForm.languageInfoGreek' })
                              ) : values?.preferredLang ? (
                                intl.formatMessage(
                                  { id: 'SignupForm.languageInfoOther' },
                                  {
                                    language:
                                      DEFAULT_LANG_OPTIONS.find(
                                        lang => lang.key === values.preferredLang
                                      )?.lang || values.preferredLang,
                                  }
                                )
                              ) : (
                                <>
                                  <strong>
                                    {intl.formatMessage({ id: 'SignupForm.languageInfoNote' })}
                                  </strong>
                                  {intl.formatMessage({ id: 'SignupForm.languageInfo' })}{' '}
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                      )}

                      {!hideCouponCode && (
                        <RenderVoucherOrId
                          userType={userType}
                          showPassword={showPassword}
                          formId={formId}
                          intl={intl}
                          values={values}
                          form={form}
                          isCustomLandingPage={isCustomLandingPage}
                          isCorporateUser={isCorporateSignup}
                          voucherInputId={couponInputId}
                          couponPlanError={couponPlanError} // <-- pass red mismatch message
                          voucherSource={initialVoucherSource}
                          autoVerifyVoucher={autoVerifyVoucher}
                        />
                      )}
                    </div>

                    {isProgram ? (
                      <>
                        <div className={css.terms}>{termsAndConditions}</div>

                        <div className={css.buttonGroup}>
                          <PrimaryButton
                            type="button"
                            className={css.subscribeBtn}
                            data-testid="register-submit"
                            disabled={
                              isBioiatriki()
                                ? submitDisabled
                                : isSubscribeDisabled ||
                                  invalidDate ||
                                  submitDisabled ||
                                  voucherVerificationInProgress
                            }
                            onClick={() => {
                              handleSubmit(values);
                            }}
                            inProgress={paymentInProgress}
                          >
                            {intl.formatMessage({ id: 'ProgramOnboardingPage.subscribeButton' })}
                          </PrimaryButton>
                        </div>
                      </>
                    ) : (
                      <div className={css.bottomWrapper}>
                        <div className={css.terms}>{termsAndConditions}</div>
                        <PrimaryButton
                          className={css.submitBtn}
                          type="submit"
                          data-testid="signup-submit"
                          onClick={() => {
                            if (userType === USER_TYPE.INDIVIDUAL) {
                              gtag('event', 'signup_attempt', {
                                send_to: 'AW-11487056950/kGGWCKD-mZAZELaouuUq',
                              });
                            }
                          }}
                          inProgress={submitInProgress}
                          disabled={
                            invalidDate ||
                            submitDisabled ||
                            voucherVerificationInProgress ||
                            (userType === USER_TYPE.DOCTOR && !values?.speciality)
                          }
                        >
                          <FormattedMessage id="SignupForm.signUp" />
                        </PrimaryButton>
                        <div className={css.alreadyHaveAccount}>
                          <FormattedMessage id="SignupForm.alreadyHaveAnAccount" />{' '}
                          <span
                            className={css.alreadyHaveAccountLink}
                            onClick={() => {
                              const baseRoute = pathByRouteName('LoginPage', routeConfiguration);
                              const currentLocale = getDefaultLocale();
                              const route = getUrlForLocale(baseRoute, currentLocale);
                              history.push({
                                pathname: route,
                                search: location.search || '',
                                state: { from: props.from },
                              });
                            }}
                          >
                            <FormattedMessage id="SignupForm.signIn" />
                          </span>
                        </div>
                      </div>
                    )}
                  </Form>
                </div>
                <div className={css.contactInfo}>
                  <h2>
                    <FormattedMessage id="SignupForm.contact.heading" />
                  </h2>
                  <p>
                    <FormattedMessage id="SignupForm.contact.blurb" />
                  </p>
                  <div className={css.email}>
                    <Icons name="emailOutline" />
                    <p>info@vivianlab.com</p>
                  </div>
                  <div className={css.email}>
                    <Icons name="phoneOutline" />
                    <p>+30 21 1199 8304</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Form className={classes} onSubmit={handleSubmit} data-testid="signup-form">
              <div className={css.signupForm}>
                <div className={css.authHead}>
                  <h1>
                    {isFromBooking ? (
                      <FormattedMessage id="AuthenticationPage.signupToBook" />
                    ) : (
                      <FormattedMessage id="AuthenticationPage.signupForm" />
                    )}
                  </h1>
                </div>

                {isProgram && !forceCorporateCoupon && (
                  <div className={css.subscriptionToggle}>
                    <button
                      type="button"
                      className={classNames(css.toggleOption, {
                        [css.active]: subscriptionUserType === 'regular',
                      })}
                      onClick={() => handleSubscriptionTypeChange('regular')}
                    >
                      {intl.formatMessage({ id: 'SignupForm.regularUser' })}
                    </button>
                    <button
                      type="button"
                      className={classNames(css.toggleOption, {
                        [css.active]: subscriptionUserType === 'corporate',
                      })}
                      onClick={() => handleSubscriptionTypeChange('corporate')}
                    >
                      {intl.formatMessage({ id: 'SignupForm.corporateUser' })}
                    </button>
                  </div>
                )}

                {!shouldHideUserTypeOptions && (
                  <div className={css.typeOptions}>
                    {userTypeOptions?.map((u, i) => (
                      <FieldRadioButton
                        key={i}
                        id={u.key}
                        name="type"
                        label={isGreek ? u.greekLabel : isEnglish ? u.label : u.germanLabel}
                        value={u.key}
                        validate={validators.required(
                          intl.formatMessage({ id: 'SignupForm.userTypeOptions' })
                        )}
                      />
                    ))}
                  </div>
                )}

                <div className={classNames(css.formRow, css.nameRow)}>
                  <div className={css.formFld}>
                    <FieldTextInput
                      type="text"
                      id={formId ? `${formId}.fname` : 'fname'}
                      name="fname"
                      autoComplete="given-name"
                      placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' })}
                      validate={validators.required(
                        intl.formatMessage({ id: 'SignupForm.firstNameRequired' })
                      )}
                    />
                  </div>
                  <div className={css.formFld}>
                    <FieldTextInput
                      type="text"
                      id={formId ? `${formId}.lname` : 'lname'}
                      name="lname"
                      autoComplete="family-name"
                      placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' })}
                      validate={validators.required(
                        intl.formatMessage({ id: 'SignupForm.lastNameRequired' })
                      )}
                    />
                  </div>
                </div>

                <div
                  className={classNames(css.formRow, css.fullWidth)}
                  style={{ position: 'relative' }}
                >
                  <div className={css.formFld}>
                    <FieldTextInput
                      type="email"
                      name="email"
                      id="email-signup"
                      data-testid="signup-email"
                      autoComplete="email"
                      placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder' })}
                      validate={emailValidatorsCombined}
                    />
                    {emailCheckLoading && (
                      <span
                        style={{
                          position: 'absolute',
                          right: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}
                      >
                        <IconSpinner />
                      </span>
                    )}
                  </div>
                </div>

                <div className={classNames(css.formRow, css.fullWidth)}>
                  <div className={css.formFld}>
                    <FieldTextInput
                      type={showPassword ? 'text' : 'password'}
                      id={formId ? `${formId}.password` : 'password'}
                      name="password"
                      data-testid="signup-password"
                      autoComplete="new-password"
                      placeholder={intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' })}
                      validate={passwordValidators}
                    />
                    <span
                      className={css.showHidePass}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <IconEyeClose /> : <IconEyeOpen />}
                    </span>
                  </div>
                </div>

                {userType === USER_TYPE.INDIVIDUAL ? renderIndividualsFields : null}
                {userType === USER_TYPE.DOCTOR ? renderDoctorFields : null}

                {!isBioiatriki() && (
                  <div className={classNames(css.formRow, css.fullWidth)}>
                    <div className={css.formFld}>
                      <FieldSelect
                        id={formId ? `${formId}.preferredLang` : 'preferredLang'}
                        name="preferredLang"
                        validate={validators.required(
                          intl.formatMessage({ id: 'SignupForm.preferredLangRequired' })
                        )}
                        onChange={handleLanguageChange}
                      >
                        {DEFAULT_LANG_OPTIONS.map(s => (
                          <option key={s.key} value={s.key} disabled={s?.disabled}>
                            {isGreek ? s.greekLabel : isEnglish ? s.label : s.germanLabel}
                          </option>
                        ))}
                      </FieldSelect>
                      <p className={css.languageInfo}>
                        {values?.preferredLang === 'gr' ? (
                          intl.formatMessage({ id: 'SignupForm.languageInfoGreek' })
                        ) : values?.preferredLang ? (
                          intl.formatMessage(
                            { id: 'SignupForm.languageInfoOther' },
                            {
                              language:
                                DEFAULT_LANG_OPTIONS.find(lang => lang.key === values.preferredLang)
                                  ?.lang || values.preferredLang,
                            }
                          )
                        ) : (
                          <>
                            <strong>
                              {intl.formatMessage({ id: 'SignupForm.languageInfoNote' })}
                            </strong>
                            {intl.formatMessage({ id: 'SignupForm.languageInfo' })}{' '}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {!hideCouponCode && (
                  <RenderVoucherOrId
                    userType={userType}
                    showPassword={showPassword}
                    formId={formId}
                    intl={intl}
                    values={values}
                    form={form}
                    isCustomLandingPage={isCustomLandingPage}
                    isCorporateUser={isCorporateSignup}
                    voucherInputId={couponInputId}
                    couponPlanError={couponPlanError} // <-- pass red mismatch message
                    voucherSource={initialVoucherSource}
                    autoVerifyVoucher={autoVerifyVoucher}
                  />
                )}
              </div>

              {isProgram ? (
                <>
                  <div className={css.terms}>{termsAndConditions}</div>

                  <div className={css.buttonGroup}>
                    <PrimaryButton
                      type="button"
                      className={css.subscribeBtn}
                      data-testid="register-submit"
                      disabled={
                        isBioiatriki()
                          ? submitDisabled
                          : isSubscribeDisabled ||
                            invalidDate ||
                            submitDisabled ||
                            voucherVerificationInProgress
                      }
                      onClick={() => {
                        handleSubmit(values);
                      }}
                      inProgress={paymentInProgress}
                    >
                      {intl.formatMessage({ id: 'ProgramOnboardingPage.subscribeButton' })}
                    </PrimaryButton>
                  </div>
                </>
              ) : (
                <div className={css.bottomWrapper}>
                  <div className={css.terms}>{termsAndConditions}</div>
                  <PrimaryButton
                    className={css.submitBtn}
                    type="submit"
                    data-testid="signup-submit"
                    onClick={() => {
                      if (userType === USER_TYPE.INDIVIDUAL) {
                        gtag('event', 'signup_attempt', {
                          send_to: 'AW-11487056950/kGGWCKD-mZAZELaouuUq',
                        });
                      }
                    }}
                    inProgress={submitInProgress}
                    disabled={
                      invalidDate ||
                      submitDisabled ||
                      voucherVerificationInProgress ||
                      (userType === USER_TYPE.DOCTOR && !values?.speciality)
                    }
                  >
                    <FormattedMessage id="SignupForm.signUp" />
                  </PrimaryButton>
                  <div className={css.alreadyHaveAccount}>
                    <FormattedMessage id="SignupForm.alreadyHaveAnAccount" />{' '}
                    <span
                      className={css.alreadyHaveAccountLink}
                      onClick={() => {
                        const baseRoute = pathByRouteName('LoginPage', routeConfiguration);
                        const currentLocale = getDefaultLocale();
                        const route = getUrlForLocale(baseRoute, currentLocale);
                        history.push({
                          pathname: route,
                          search: location.search || '',
                          state: { from: props.from },
                        });
                      }}
                    >
                      <FormattedMessage id="SignupForm.signIn" />
                    </span>
                  </div>
                </div>
              )}
            </Form>
          )}
        </>
      );
    }}
  />
);

SignupFormComponent.defaultProps = {
  inProgress: false,
  subscriptionUserType: 'regular',
};

SignupFormComponent.propTypes = {
  inProgress: bool,
  termsAndConditions: node.isRequired,
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
